import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { type VariantProps } from "class-variance-authority";
import { IconCheck, IconChevronDown, IconSearch } from "@tabler/icons-react";
import { inputVariants } from "./Input";
import { cn } from "../../lib/utils";
import { searchLabels } from "../../domains/app/labelSearch";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SelectOption {
  label: string;
  value: string;
}

type SelectProps<T extends SelectOption = SelectOption> = VariantProps<
  typeof inputVariants
> & {
  /** Currently selected value */
  value: string;
  /** Called when user picks an option */
  onChange: (option: T) => void;
  /** Full list of options */
  options: T[];
  /** Placeholder when nothing is selected */
  placeholder?: string;
  /** Group options under a header. Return a group label per option. */
  groupBy?: (option: T) => string;
  /** Render custom content for an option row */
  renderOption?: (option: T, isSelected: boolean) => ReactNode;
  /**
   * One action pinned under the scrolling options (Figma "Select / Footer
   * action"), or nothing. The search does not filter it. `panel` says what
   * picking it does to the panel before `onSelect` runs: `close` closes it,
   * `stay-open` keeps it open and clears the search, e.g. when the action adds
   * options the query would hide.
   */
  footerAction: {
    label: string;
    onSelect: () => void;
    panel: "close" | "stay-open";
  } | null;
  /**
   * Options the search also matches, listed only while a query is typed, or
   * nothing. They are not listed when the search is empty.
   */
  searchOnlyOptions: T[] | null;
  /** Extra class names on the root container */
  className?: string;
  disabled?: boolean;
} & (
    | {
        /**
         * The form field, as wide as its container, its panel as wide as it.
         */
        appearance: "field";
        /** Enable inline search to filter options */
        searchable?: boolean;
        /** Search placeholder (only when `searchable`) */
        searchPlaceholder?: string;
        /** Minimum characters before showing options (requires `searchable`) */
        minSearchLength?: number;
      }
    | {
        /**
         * A label and a chevron sized to the label, for a page or bar header,
         * its panel at least `INLINE_PANEL_WIDTH` wide. Never searchable: the
         * header has no room for a search field.
         */
        appearance: "inline";
      }
  );

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** The inline panel's width, wider than the label that opens it (Figma). */
const INLINE_PANEL_WIDTH = 360;
/** The room an inline panel leaves to the viewport's right edge. */
const INLINE_PANEL_GUTTER = 16;

function SelectInner<T extends SelectOption = SelectOption>(
  props: SelectProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const {
    value,
    onChange,
    options,
    placeholder,
    groupBy,
    renderOption,
    appearance,
    footerAction,
    searchOnlyOptions,
    variant,
    className,
    disabled = false,
  } = props;
  // Only the field searches: the inline variant carries no search props.
  const searchable = props.appearance === "field" && props.searchable === true;
  const searchPlaceholder =
    props.appearance === "field" ? props.searchPlaceholder : undefined;
  const minSearchLength =
    props.appearance === "field" ? props.minSearchLength : undefined;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Position the dropdown relative to the trigger (non-searchable portal only)
  useLayoutEffect(() => {
    if (!open || searchable || triggerRef.current === null) return;

    function updatePosition(): void {
      if (triggerRef.current === null) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width:
          appearance === "field"
            ? rect.width
            : Math.min(
                Math.max(rect.width, INLINE_PANEL_WIDTH),
                window.innerWidth - rect.left - INLINE_PANEL_GUTTER
              ),
      });
    }

    updatePosition();

    // Reposition on scroll / resize (any ancestor could scroll)
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, searchable, appearance]);

  // Close on outside click
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent): void {
      const target = e.target;
      if (!(target instanceof Node)) return;
      // In searchable mode, check the container div (which holds both input and dropdown)
      if (
        containerRef.current !== null &&
        containerRef.current.contains(target)
      )
        return;
      // In non-searchable mode, check trigger and portal dropdown separately
      if (triggerRef.current !== null && triggerRef.current.contains(target))
        return;
      if (dropdownRef.current !== null && dropdownRef.current.contains(target))
        return;
      setOpen(false);
      setSearch("");
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        // Handled: a later listener (the composer's Escape-to-stop) runs
        // after this close has already removed the panel.
        e.preventDefault();
        setOpen(false);
        setSearch("");
        // Back on the trigger: the panel's search field held the focus, and
        // it is gone.
        if (triggerRef.current !== null) triggerRef.current.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (minSearchLength !== undefined && search.length < minSearchLength) {
      return [];
    }
    return searchLabels({
      items:
        searchOnlyOptions === null || search.trim() === ""
          ? options
          : [...options, ...searchOnlyOptions],
      query: search,
      labelOf: (o) => o.label,
    });
  }, [options, searchOnlyOptions, search, minSearchLength]);

  const grouped = useMemo(() => {
    if (groupBy === undefined) return null;
    const map = new Map<string, T[]>();
    for (const opt of filtered) {
      const group = groupBy(opt);
      const arr = map.get(group);
      if (arr !== undefined) {
        arr.push(opt);
      } else {
        map.set(group, [opt]);
      }
    }
    return map;
  }, [filtered, groupBy]);

  const handleSelect = useCallback(
    (option: T) => {
      onChange(option);
      setOpen(false);
      setSearch("");
      if (triggerRef.current !== null) triggerRef.current.focus();
    },
    [onChange]
  );

  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);

  const toggleOpen = useCallback(() => {
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  }, [disabled]);

  // Merge external ref with internal ref
  const setRefs = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref !== null) {
        ref.current = node;
      }
    },
    [ref]
  );

  // -- Options list (shared between searchable inline dropdown and portal) --
  const optionsList = (
    <>
      {grouped !== null
        ? renderGrouped(grouped, value, handleSelect, renderOption)
        : renderFlat(filtered, value, handleSelect, renderOption)}

      {filtered.length === 0 && (
        <div className="px-4 py-3 text-center font-['DM_Sans',sans-serif] text-[14px] text-[#929292]">
          {minSearchLength !== undefined && search.length < minSearchLength
            ? (searchPlaceholder ?? "Type to search...")
            : "No results"}
        </div>
      )}
    </>
  );

  // Pinned under the scrolling list, so a long list never pushes it out of view.
  const footerNode =
    footerAction === null ? null : (
      <button
        type="button"
        className={cn(
          "w-full cursor-pointer border-t border-ds-slate-100 px-4 py-2.5 text-left",
          "font-dm-sans text-ui-body font-medium text-ds-primary",
          "transition-colors duration-100 hover:bg-[rgba(0,0,0,0.04)]"
        )}
        onClick={() => {
          switch (footerAction.panel) {
            case "close":
              close();
              break;
            case "stay-open":
              setSearch("");
              break;
            default:
              throw new Error(footerAction.panel satisfies never);
          }
          footerAction.onSelect();
        }}
      >
        {footerAction.label}
      </button>
    );

  // -----------------------------------------------------------------------
  // Searchable mode (Figma "Select / Option"): the closed field is the same
  // trigger as the plain mode, and the search is the first row of the open
  // panel. The panel is inline (absolutely positioned, no portal), so the
  // search input and the options share the container the outside click reads.
  // -----------------------------------------------------------------------
  if (searchable) {
    return (
      <div ref={containerRef} className="relative">
        <button
          ref={setRefs}
          type="button"
          disabled={disabled}
          onClick={toggleOpen}
          aria-expanded={open}
          className={cn(
            inputVariants({ variant }),
            "w-full cursor-pointer px-4 py-3",
            "focus:outline-none focus-visible:border-ds-slate-400",
            open && "border-ds-slate-400",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "flex-1 min-w-0 text-left truncate whitespace-pre",
                "font-dm-sans text-ui-body font-normal",
                "font-features-['salt'_1]",
                selectedOption !== null
                  ? "text-ds-slate-950"
                  : "text-[rgba(122,109,101,0.5)]"
              )}
            >
              {selectedOption !== null ? selectedOption.label : placeholder}
            </span>
            <IconChevronDown
              size={18}
              className={cn(
                "shrink-0 text-ds-slate-950 transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </div>
        </button>

        {open && (
          <div
            ref={dropdownRef}
            className={cn(
              "absolute inset-x-0 top-full z-50 mt-1",
              "flex flex-col",
              "bg-white rounded-xl border border-ds-slate-200",
              "shadow-popover",
              "overflow-hidden",
              "animate-in fade-in slide-in-from-top-1 duration-150"
            )}
          >
            <label className="flex items-center gap-1 px-4 py-2.5">
              <IconSearch
                size={16}
                className="shrink-0 text-ds-slate-500"
                aria-hidden
              />
              <input
                type="text"
                autoFocus
                value={search}
                placeholder={searchPlaceholder ?? placeholder}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Enter takes the option typed in full, or else the first
                  // match, and never submits the form the field sits in. "In
                  // full" reads the label past what leads it and is not a
                  // letter: a country's flag, which nobody types.
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  const typed = search.trim().toLocaleLowerCase();
                  const exact = filtered.find(
                    (option) =>
                      option.label
                        .replace(/^\P{L}+/u, "")
                        .trim()
                        .toLocaleLowerCase() === typed
                  );
                  const chosen = exact === undefined ? filtered.at(0) : exact;
                  if (chosen !== undefined) handleSelect(chosen);
                }}
                className={cn(
                  "flex-1 min-w-0 bg-transparent focus:outline-none",
                  "font-dm-sans text-ui-note font-medium text-ds-slate-950",
                  "placeholder:text-ds-slate-500"
                )}
              />
            </label>
            <div className="min-h-0 max-h-70 flex-1 overflow-y-auto overscroll-contain scrollbar-subtle pb-1">
              {optionsList}
            </div>
            {footerNode}
          </div>
        )}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Non-searchable mode: portal dropdown, opened by the field or by the inline
  // trigger (the selected label and a chevron, no field around them).
  // -----------------------------------------------------------------------
  const trigger =
    appearance === "inline" ? (
      <button
        ref={setRefs}
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        aria-expanded={open}
        className={cn(
          "flex max-w-full min-w-0 cursor-pointer items-center gap-1.5 text-ds-slate-700",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
      >
        <span className="min-w-0 truncate text-ui-body font-semibold">
          {selectedOption !== null ? selectedOption.label : placeholder}
        </span>
        <IconChevronDown
          size={16}
          stroke={2}
          className={cn(
            "shrink-0 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>
    ) : (
      <button
        ref={setRefs}
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        className={cn(
          inputVariants({ variant }),
          "w-full cursor-pointer px-4 py-3",
          "focus:outline-none focus:border-ds-black focus:shadow-[inset_0_0_0_2px_var(--color-ds-black)]",
          open &&
            "border-ds-black shadow-[inset_0_0_0_2px_var(--color-ds-black)]",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
      >
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "flex-1 min-w-0 text-left truncate",
              "font-['DM_Sans',sans-serif] text-[15px] leading-[1.4] font-normal",
              "tracking-[-0.15px]",
              "font-features-['salt'_1]",
              selectedOption !== null
                ? "text-[#2d2d2d]"
                : "text-[rgba(122,109,101,0.5)]"
            )}
          >
            {selectedOption !== null ? selectedOption.label : placeholder}
          </span>
          <IconChevronDown
            size={18}
            className={cn(
              "shrink-0 text-[#7a6d65] transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </div>
      </button>
    );

  return (
    <div className={cn("relative", appearance === "inline" && "min-w-0")}>
      {trigger}

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className={cn(
              "z-9999",
              "flex flex-col",
              "bg-white rounded-xl border border-[rgba(0,0,0,0.1)]",
              "shadow-popover",
              "overflow-hidden",
              "animate-in fade-in slide-in-from-top-1 duration-150"
            )}
          >
            <div className="min-h-0 max-h-[280px] flex-1 overflow-y-auto overscroll-contain scrollbar-subtle py-1">
              {optionsList}
            </div>
            {footerNode}
          </div>,
          document.body
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Renderers
// ---------------------------------------------------------------------------

function renderFlat<T extends SelectOption>(
  options: readonly T[],
  selectedValue: string,
  onSelect: (o: T) => void,
  renderOption?: (o: T, selected: boolean) => ReactNode
): ReactNode {
  return options.map((option) => {
    const isSelected = option.value === selectedValue;
    return (
      <OptionRow
        key={option.value}
        option={option}
        isSelected={isSelected}
        onSelect={onSelect}
        renderOption={renderOption}
      />
    );
  });
}

function renderGrouped<T extends SelectOption>(
  grouped: Map<string, T[]>,
  selectedValue: string,
  onSelect: (o: T) => void,
  renderOption?: (o: T, selected: boolean) => ReactNode
): ReactNode {
  return [...grouped.entries()].map(([groupLabel, groupOptions]) => (
    <div key={groupLabel}>
      <div className="px-4 pt-2.5 pb-1 font-['DM_Sans',sans-serif] text-[11px] font-semibold uppercase tracking-wide text-[#929292]">
        {groupLabel}
      </div>
      {groupOptions.map((option) => {
        const isSelected = option.value === selectedValue;
        return (
          <OptionRow
            key={option.value}
            option={option}
            isSelected={isSelected}
            onSelect={onSelect}
            renderOption={renderOption}
          />
        );
      })}
    </div>
  ));
}

function OptionRow<T extends SelectOption>({
  option,
  isSelected,
  onSelect,
  renderOption,
}: {
  option: T;
  isSelected: boolean;
  onSelect: (o: T) => void;
  renderOption?: (o: T, selected: boolean) => ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      className={cn(
        "flex items-center gap-2 w-full px-4 py-2.5 text-left cursor-pointer",
        "transition-colors duration-100",
        "hover:bg-[rgba(0,0,0,0.04)]",
        isSelected && "bg-[rgba(0,0,0,0.04)]"
      )}
      onClick={() => {
        onSelect(option);
      }}
    >
      {renderOption !== undefined ? (
        renderOption(option, isSelected)
      ) : (
        <>
          <span
            className={cn(
              // `whitespace-pre`: a label's own spacing is kept, as in the
              // closed field (the country's flag is followed by two spaces).
              "flex-1 min-w-0 truncate whitespace-pre",
              "font-['DM_Sans',sans-serif] text-[15px] leading-[1.4] font-normal",
              "tracking-[-0.15px] text-[#2d2d2d]",
              "font-features-['salt'_1]"
            )}
          >
            {option.label}
          </span>
          {isSelected && (
            <IconCheck size={16} className="shrink-0 text-ds-primary" />
          )}
        </>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Export (forwardRef with generics)
// ---------------------------------------------------------------------------

const Select = forwardRef(SelectInner) as <
  T extends SelectOption = SelectOption,
>(
  props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => ReturnType<typeof SelectInner>;

export { Select, type SelectOption, type SelectProps };
